/**
 * Ring Chat API helpers
 *
 * Async chat (text + audio) between visitor and resident during a door ring.
 * Polling-based: GET every 3s for new messages. POST for sending.
 * No auth — anyone with the ringId (256-bit random token) can read/send.
 *
 * GameBoy edition: uses visitsApi from this app's lib/api.ts for the
 * polling endpoint (which works with or without auth). POST endpoints
 * (send text/audio, accept/reject/end ring) use the API base directly
 * because they accept an unauthenticated sender tag in the body.
 */

import { getApiBase, visitsApi } from './api';

export type Sender = 'visitor' | 'resident';

export interface ChatMessage {
  id: string;
  ring_id: string;
  sender: Sender;
  text: string | null;
  has_audio: boolean;
  audio_mime: string | null;
  created_at: string;
}

export interface RingStatus {
  id: string;
  // Mirrors the CHECK constraint on door_rings.status (migration 0024):
  //   'ringing' | 'answered' | 'opened_door' | 'rejected' | 'timeout' | 'cancelled'
  // 'accepted' / 'ended' are timestamp columns on door_rings
  // (accepted_at / ended_at) — they are NOT valid status values.
  status: 'ringing' | 'answered' | 'opened_door' | 'rejected' | 'timeout' | 'cancelled';
  accepted_at: string | null;
  rejected_at: string | null;
  ended_at: string | null;
  expires_at: string;
}

export interface PollResult {
  ring: RingStatus;
  messages: ChatMessage[];
  server_ts: string;
}

export class RingChatError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'RingChatError';
    this.status = status;
    this.data = data;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data: { error?: unknown } = await res.json().catch(() => ({}));
  if (!res.ok) {
    // `data.error` is usually a string, but Hono's zValidator returns a
    // ZodError object like { issues: [...], name: 'ZodError' } on schema
    // failures (e.g. audio_base64 > 70000 chars). Without this extraction,
    // the user sees "! [object Object]" in the chat error banner because
    // `data.error || "HTTP ..."` returns the truthy object as `message`.
    const errorMsg = extractErrorMessage(data?.error, res.status);
    throw new RingChatError(errorMsg, res.status, data);
  }
  return data as T;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`);
  if (!res.ok) {
    const data: { error?: unknown } = await res.json().catch(() => ({}));
    throw new RingChatError(extractErrorMessage(data?.error, res.status), res.status, data);
  }
  return res.json() as Promise<T>;
}

/**
 * Coerce any error payload (string | ZodError | other object) into a
 * human-readable single-line string. Falls back to "HTTP <status>" if we
 * can't extract anything meaningful.
 */
function extractErrorMessage(err: unknown, status: number): string {
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; issues?: Array<{ message?: string; path?: (string | number)[] }> };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
    if (Array.isArray(e.issues) && e.issues.length > 0) {
      return e.issues
        .map((i) => {
          const where = i.path && i.path.length > 0 ? i.path.join('.') : 'campo';
          return `${where}: ${i.message || 'inválido'}`;
        })
        .join('; ');
    }
  }
  return `HTTP ${status}`;
}

export async function pollMessages(ringId: string, since: string): Promise<PollResult> {
  // Use visitsApi.messages (authed request) so the same auth/refresh
  // machinery handles token expiry. The Worker ignores the extra header.
  return visitsApi.messages(ringId, since) as Promise<PollResult>;
}

export async function sendText(ringId: string, sender: Sender, text: string): Promise<ChatMessage> {
  const res = await postJson<{ success: boolean; message: ChatMessage }>(
    `/visits/rings/${ringId}/messages`,
    { sender, text }
  );
  return res.message;
}

export async function sendAudio(
  ringId: string,
  sender: Sender,
  audioBase64: string,
  audioMime: string
): Promise<ChatMessage> {
  const res = await postJson<{ success: boolean; message: ChatMessage }>(
    `/visits/rings/${ringId}/messages`,
    { sender, audio_base64: audioBase64, audio_mime: audioMime }
  );
  return res.message;
}

export async function fetchAudio(ringId: string, msgId: string): Promise<string> {
  // Returns the base64 audio for a single message (decoded client-side).
  const res = await getJson<{ success: boolean; audio_base64: string; audio_mime: string }>(
    `/visits/rings/${ringId}/messages/${msgId}/audio`
  );
  return res.audio_base64;
}

export async function acceptRing(ringId: string): Promise<void> {
  await postJson(`/visits/rings/${ringId}/accept`, {});
}

export async function rejectRing(ringId: string): Promise<void> {
  await postJson(`/visits/rings/${ringId}/reject`, {});
}

export async function endRing(ringId: string): Promise<void> {
  await postJson(`/visits/rings/${ringId}/end`, {});
}

export function base64ToBlob(base64: string, mime: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip "data:...;base64," prefix
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
